import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const FeaturesDetail = () => {
  const [activeTab, setActiveTab] = useState('staff');

  const features = {
    staff: {
      title: "Staff Management Suite",
      icon: "👥",
      description: "Complete staff administration, time tracking, and scheduling system",
      items: [
        {
          name: "Digital Time Clock",
          description: "Automated clock-in/out system with shift tracking",
          benefits: ["Eliminates manual timesheets", "Real-time payroll data", "Shift handoff notes"]
        },
        {
          name: "Smart Scheduling",
          description: "Conflict detection and automated schedule management",
          benefits: ["Prevents double-booking", "Multiple shifts per day", "Visual schedule overview"]
        },
        {
          name: "Payroll Integration", 
          description: "Automated time calculation and reporting",
          benefits: ["Export to payroll systems", "Overtime tracking", "Department breakdown"]
        },
        {
          name: "Role-Based Access",
          description: "Granular permissions for different staff levels",
          benefits: ["Admin, Staff, Case Worker roles", "Secure password management", "Activity logging"]
        },
        {
          name: "Shift Communication",
          description: "Seamless handoffs between shifts",
          benefits: ["Shift notes and updates", "Critical information transfer", "Incident reporting"]
        }
      ]
    },
    residents: {
      title: "Resident Services",
      icon: "🏠",
      description: "HMIS-compliant resident management and case work tools",
      items: [
        {
          name: "HMIS-Compliant Intake",
          description: "Streamlined intake process meeting all federal requirements",
          benefits: ["Pre-built HMIS forms", "Automatic data validation", "Digital signatures"]
        },
        {
          name: "Case Notes Management",
          description: "Comprehensive case documentation with file attachments",
          benefits: ["Rich text notes", "File upload support", "Search and filtering"]
        },
        {
          name: "Room Assignment",
          description: "Intelligent bed and room management system",
          benefits: ["Real-time occupancy tracking", "Conflict prevention", "Waitlist management"]
        },
        {
          name: "Status Tracking",
          description: "Complete resident journey management", 
          benefits: ["Current, past, banned status", "30-day re-entry rules", "Departure tracking"]
        },
        {
          name: "Demographics & Reporting",
          description: "Detailed resident analytics and compliance reporting",
          benefits: ["Age, gender, ethnicity tracking", "Length of stay analysis", "Outcome reporting"]
        }
      ]
    },
    facilities: {
      title: "Facility Operations",
      icon: "🛏️",
      description: "Real-time facility management and maintenance tracking",
      items: [
        {
          name: "Room Management",
          description: "Multi-bed room tracking with occupancy monitoring",
          benefits: ["Bed-level availability", "Room capacity planning", "Assignment history"]
        },
        {
          name: "Maintenance Requests",
          description: "Priority-based maintenance and repair tracking",
          benefits: ["Photo documentation", "Priority levels", "Completion tracking"]
        },
        {
          name: "Occupancy Analytics",
          description: "Real-time bed availability and utilization reports",
          benefits: ["Live capacity dashboard", "Historical trends", "Forecasting tools"]
        },
        {
          name: "Chore Management",
          description: "Resident chore assignment and completion tracking",
          benefits: ["Fair rotation system", "Completion verification", "Point tracking"]
        },
        {
          name: "Laundry Scheduling",
          description: "Organized laundry access and scheduling system", 
          benefits: ["Time slot booking", "Usage tracking", "Equipment monitoring"]
        }
      ]
    },
    meals: {
      title: "Meal Service Tracking",
      icon: "🍽️", 
      description: "Comprehensive meal planning, tracking, and reporting system",
      items: [
        {
          name: "Daily Meal Planning",
          description: "Plan and track all meal services throughout the day",
          benefits: ["Breakfast, lunch, dinner tracking", "Special dietary needs", "Portion planning"]
        },
        {
          name: "Service Differentiation",
          description: "Separate tracking for residents vs community members",
          benefits: ["Resident vs guest tracking", "Funding source reporting", "Demographics analysis"]
        },
        {
          name: "Monthly Reporting",
          description: "Comprehensive monthly meal service analytics",
          benefits: ["Detailed statistics", "Demographic breakdowns", "Export capabilities"]
        },
        {
          name: "Nutrition Planning",
          description: "Meal planning with nutritional considerations",
          benefits: ["Balanced meal tracking", "Special dietary accommodations", "Cost analysis"]
        },
        {
          name: "Inventory Integration",
          description: "Connect meal planning with food inventory management",
          benefits: ["Ingredient tracking", "Cost per meal", "Waste reduction"]
        }
      ]
    },
    reporting: {
      title: "Analytics & Reporting",
      icon: "📊",
      description: "Comprehensive data analytics and compliance reporting tools",
      items: [
        {
          name: "Real-Time Dashboard",
          description: "Live overview of all shelter operations and key metrics",
          benefits: ["Current occupancy", "Staff on duty", "Daily statistics"]
        },
        {
          name: "HMIS Compliance",
          description: "Built-in reporting to meet all federal requirements",
          benefits: ["Automated APR generation", "Data quality checks", "Export formats"]
        },
        {
          name: "Custom Reports",
          description: "Flexible reporting system for organizational needs",
          benefits: ["Drag-and-drop builder", "Scheduled reports", "Multiple formats"]
        },
        {
          name: "Funding Reports",
          description: "Grant and funding compliance reporting",
          benefits: ["Outcome tracking", "Performance metrics", "Funder-specific formats"]
        },
        {
          name: "Incident Tracking",
          description: "Safety and incident documentation and analysis",
          benefits: ["Incident logging", "Trend analysis", "Safety reporting"]
        }
      ]
    }
  };

  return (
    <div className="py-12">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 text-center mb-16">
        <h1 className="text-4xl font-bold mb-4">
          Complete Shelter Management Features
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Shelter Care Connect provides everything you need to run your shelter efficiently. 
          Explore our comprehensive feature set designed specifically for homeless services.
        </p>
        <Link 
          to="/beta" 
          className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold inline-block"
        >
          Try These Features - Join Beta Testing
        </Link>
      </section>

      {/* Feature Navigation Tabs */}
      <section className="max-w-7xl mx-auto px-4 mb-12">
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {Object.entries(features).map(([key, feature]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-6 py-3 rounded-lg font-semibold transition duration-200 ${
                activeTab === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-2">{feature.icon}</span>
              {feature.title}
            </button>
          ))}
        </div>
      </section>

      {/* Active Feature Detail */}
      <section className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-4">{features[activeTab].icon}</span>
              <div>
                <h2 className="text-3xl font-bold">{features[activeTab].title}</h2>
                <p className="text-blue-100 text-lg">{features[activeTab].description}</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {features[activeTab].items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition duration-200">
                  <h3 className="text-xl font-semibold mb-3 text-blue-800">{item.name}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  
                  <h4 className="font-semibold text-gray-800 mb-2">Key Benefits:</h4>
                  <ul className="space-y-1">
                    {item.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start">
                        <span className="text-green-500 mr-2 mt-1">✓</span>
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration Benefits */}
      <section className="bg-gray-50 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Why Integration Matters
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔗</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">All-in-One Platform</h3>
              <p className="text-gray-600">
                No more juggling multiple systems. Everything you need in one integrated platform.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Real-Time Updates</h3>
              <p className="text-gray-600">
                Changes sync instantly across all modules. No delays, no duplicate data entry.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Mobile Accessible</h3>
              <p className="text-gray-600">
                Access from any device - desktop, tablet, or phone. Perfect for staff on the go.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Secure & Compliant</h3>
              <p className="text-gray-600">
                HMIS compliant with role-based security. Your data is protected and audit-ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Return on Investment
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-green-50 p-8 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">5-15 Hours</div>
              <div className="text-green-800 font-semibold mb-2">Time Saved Weekly</div>
              <div className="text-green-700 text-sm">
                Automated processes eliminate manual paperwork and reduce administrative burden
              </div>
            </div>
            
            <div className="bg-blue-50 p-8 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-blue-800 font-semibold mb-2">HMIS Compliance</div>
              <div className="text-blue-700 text-sm">
                Built-in compliance features ensure you never miss required reporting
              </div>
            </div>
            
            <div className="bg-purple-50 p-8 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">Real-Time</div>
              <div className="text-purple-800 font-semibold mb-2">Data Access</div>
              <div className="text-purple-700 text-sm">
                Instant access to critical information improves decision-making and outcomes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Technical Specifications
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">System Requirements</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Modern web browser (Chrome, Firefox, Safari, Edge)</li>
                <li>• Reliable internet connection</li>
                <li>• No software installation required</li>
                <li>• Mobile and tablet compatible</li>
                <li>• Cloud-based with automatic backups</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Security & Compliance</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• HMIS-compliant data collection</li>
                <li>• Role-based access control</li>
                <li>• Encrypted data transmission</li>
                <li>• Regular security updates</li>
                <li>• Audit trail and logging</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Experience These Features?
          </h2>
          <p className="text-xl mb-8">
            Join our beta testing program and get free access to all features for 3 months
          </p>
          <div className="space-x-4">
            <Link 
              to="/beta" 
              className="bg-green-500 hover:bg-green-600 px-8 py-4 rounded-lg text-lg font-semibold inline-block"
            >
              Apply for Beta Testing
            </Link>
            <Link 
              to="/" 
              className="bg-transparent border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold inline-block"
            >
              Back to Homepage
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesDetail;